import type { ExerciseResult } from "./schemas/sessionResult.js";
import type { SessionResultPriorRecord } from "./sessionResult.js";
import { isVolumeSessionPR } from "./pr.js";

// The part of an ExerciseResult judged against earlier sessions: the sticky
// PR flags and the deltas. Split out of computeSessionResult (#116) so a row
// whose own numbers are right can have just this part re-judged when an
// earlier row is corrected, without recomputing its volume.
export type PriorComparison = Pick<
  ExerciseResult,
  | "isPrE1rm"
  | "isPrVolume"
  | "deltaE1rmKg"
  | "deltaVolumeKg"
  | "deltaTopReps"
  | "previousSessionId"
>;

export function compareWithPriors(
  current: {
    topE1rmKg: number | null;
    totalVolumeKg: number;
    setCount: number;
    topSetReps: number | null;
  },
  priors: ReadonlyArray<SessionResultPriorRecord>,
): PriorComparison {
  const { topE1rmKg, totalVolumeKg, setCount, topSetReps } = current;
  let mostRecentPrior: SessionResultPriorRecord | null = null;
  let maxPriorE1rm: number | null = null;
  for (const p of priors) {
    if (
      mostRecentPrior === null ||
      new Date(p.startedAt).getTime() >
        new Date(mostRecentPrior.startedAt).getTime()
    ) {
      mostRecentPrior = p;
    }
    if (p.topE1rmKg !== null) {
      if (maxPriorE1rm === null || p.topE1rmKg > maxPriorE1rm) {
        maxPriorE1rm = p.topE1rmKg;
      }
    }
  }

  const isPrE1rm =
    topE1rmKg !== null &&
    (maxPriorE1rm === null
      ? // First-ever e1RM-eligible session for this exercise: count as
        // PR only if there was no prior session at all. A prior session
        // with no eligible sets (cardio-only, all failed) shouldn't
        // erroneously promote this one to "first ever".
        priors.length === 0
      : topE1rmKg > maxPriorE1rm);

  const isPrVolume = isVolumeSessionPR({
    currentVolumeKg: totalVolumeKg,
    currentSetCount: setCount,
    history: priors.map((p) => ({
      volumeKg: p.totalVolumeKg,
      setCount: p.setCount,
    })),
  });

  const deltaE1rmKg =
    mostRecentPrior && mostRecentPrior.topE1rmKg !== null && topE1rmKg !== null
      ? topE1rmKg - mostRecentPrior.topE1rmKg
      : null;
  const deltaVolumeKg = mostRecentPrior
    ? totalVolumeKg - mostRecentPrior.totalVolumeKg
    : null;
  const deltaTopReps =
    mostRecentPrior && mostRecentPrior.topSetReps !== null && topSetReps !== null
      ? topSetReps - mostRecentPrior.topSetReps
      : null;

  return {
    isPrE1rm,
    isPrVolume,
    deltaE1rmKg,
    deltaVolumeKg,
    deltaTopReps,
    previousSessionId: mostRecentPrior?.sessionId ?? null,
  };
}
